import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StudentAnswerContext } from '../context/StudentAnswerContext';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { AuthContext } from '../context/AuthContext';
import AnswerTextArea from '../atoms/AnswerTextArea';
import ReferencesModal from '../atoms/ReferencesModal';
import LongAnswerGradeBox from '../atoms/LongAnswerGradeBox';  // Importing LongAnswerGradeBox
import LongAnswerGradeDisplay from '../atoms/LongAnswerGradeDisplay';  // Importing LongAnswerGradeDisplay
import IndividualQuestionStudentHistory from './IndividualQuestionStudentHistory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faCommentDots, faTrash, faPlus, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';

function LongAnswerQuestion({
  questionID,
  username,
  answersReadOnly,
  gradeEdit,
  gradeDisplay,
  evaluationID,
  userAnswers,
  showHistoryButton = false,
  onOpenHistory,
  historyCount = 0,
}) {
  const {
    courseDesignState,
    handleChange: updateCourseDesignItem,
    removeFromDatabase,
  } = useContext(CourseDesignContext);
  const { handleAnswerChange } = useContext(StudentAnswerContext);
 // const studentAnswers = useMemo(() => studentAnswersState[username] || [], [studentAnswersState, username]);

  const [localAnswer, setLocalAnswer] = useState('');
  const [showWordLimitWarning, setShowWordLimitWarning] = useState(false);
  const [showReferencesModal, setShowReferencesModal] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [copiedCommentId, setCopiedCommentId] = useState(null);
  const [copyError, setCopyError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isClosingHistory, setIsClosingHistory] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState(null);
 // console.log('student answers state:', studentAnswersState);

  const addPopoverRef = useRef(null);
  const editPopoverRef = useRef(null);
  const authContext = useContext(AuthContext) || {};
  const { isInstructor } = authContext;
  const canManageSavedComments = Boolean(isInstructor);
  const adjustTextareaHeight = useCallback((textarea) => {
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  const closeModals = useCallback(() => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditCommentId(null);
    setEditCommentText('');
    setNewCommentText('');
    setPendingDeleteCommentId(null);
  }, []);

  const question = courseDesignState.find(item => item.dataID === questionID && item.dataType === 'question');
  const isGradingMode = useMemo(() => Boolean(evaluationID) && !gradeDisplay, [evaluationID, gradeDisplay]);

  const savedComments = useMemo(() => {
    return courseDesignState
      .filter(
        (item) =>
          item?.dataType === 'savedComment' &&
          item?.questionLogic === questionID &&
          typeof item?.dataText === 'string'
      )
      .slice()
      .sort((a, b) => a.dataText.localeCompare(b.dataText));
  }, [courseDesignState, questionID]);
  
  // Get word limit settings
  const hasWordLimits = courseDesignState.find(item => item?.dataType === 'hasWordLimits')?.dataText === 'Yes';
  const wordLimit = hasWordLimits ? parseInt(courseDesignState.find(item => item?.dataType === 'essayWordLimit')?.dataText) || 1000 : null;
  
  // Get references settings
  const hasReferences = courseDesignState.find(item => item?.dataType === 'hasReferences')?.dataText === 'Yes';

  // Function to separate essay content from references
  const separateContentAndReferences = (fullAnswer) => {
    if (!fullAnswer) return { content: '', references: '' };
    const parts = fullAnswer.split('|||||');
    return {
      content: parts[0] || '',
      references: parts[1] || ''
    };
  };

  // Function to combine content and references
  const combineContentAndReferences = (content, references) => {
    if (!references || references.trim() === '') {
      return content;
    }
    return `${content}|||||${references}`;
  };

  // Function to count words (excluding references)
  const countWords = (text) => {
    if (!text || typeof text !== 'string') return 0;
    const { content } = separateContentAndReferences(text);
    return content.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  useEffect(() => {
    const existingAnswer = userAnswers.find(answer => answer.questionID === questionID);
    if (existingAnswer) {
      const { content } = separateContentAndReferences(existingAnswer.questionEssayAnswer || '');
      setLocalAnswer(content);
    }
  }, [userAnswers, questionID]);
  useEffect(() => {
    if (!isEditModalOpen) {
      return;
    }
    if (editCommentId) {
      const existingComment = savedComments.find((comment) => comment.dataID === editCommentId);
      if (!existingComment) {
        closeModals();
      } else {
        setEditCommentText(existingComment.dataText || '');
      }
    }
  }, [closeModals, editCommentId, isEditModalOpen, savedComments]);

  const handleEssayChange = (newAnswer) => {
    // If word limits are enabled, check the word count (excluding references)
    if (hasWordLimits && wordLimit) {
      const wordCount = countWords(newAnswer);

      if (wordCount > wordLimit) {
        // Don't allow the change if it exceeds the limit
        setShowWordLimitWarning(true);
        return;
      } else {
        // Hide warning if word count is now within limit
        setShowWordLimitWarning(false);
      }
    }

    setLocalAnswer(newAnswer);

    // Get current references and combine with new content
    const existingAnswer = userAnswers.find((answer) => answer.questionID === questionID);
    const { references } = separateContentAndReferences(existingAnswer?.questionEssayAnswer || '');
    const fullAnswer = combineContentAndReferences(newAnswer, references);

    handleAnswerChange(username, questionID, fullAnswer, true);
  };

  // Handle saving references
  const handleSaveReferences = (newReferences) => {
    const { content } = separateContentAndReferences(userAnswers.find((answer) => answer.questionID === questionID)?.questionEssayAnswer || '');
    const fullAnswer = combineContentAndReferences(content || localAnswer, newReferences);
    handleAnswerChange(username, questionID, fullAnswer, true);
  };

  const generateRandomId = useCallback(() => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  }, []);

  const handleCopyComment = useCallback(
    async (commentText, commentId) => {
      if (!commentText) {
        return;
      }

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(commentText);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = commentText;
          textArea.style.position = 'fixed';
          textArea.style.top = '-1000px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }

        setCopiedCommentId(commentId);
        setCopyError(null);
        setTimeout(() => setCopiedCommentId(null), 1500);
      } catch (error) {
        console.error('Unable to copy saved comment:', error);
        setCopyError('Unable to copy comment. Please copy manually.');
        setTimeout(() => setCopyError(null), 3000);
      }
    },
    []
  );

  const saveNewComment = useCallback(() => {
    if (!canManageSavedComments) {
      return;
    }
    const trimmedText = newCommentText.trim();
    if (!trimmedText) {
      setIsAddModalOpen(false);
      setNewCommentText('');
      return;
    }

    const existingIds = new Set(savedComments.map((comment) => comment.dataID));
    let newId = generateRandomId();
    while (existingIds.has(newId)) {
      newId = generateRandomId();
    }

    updateCourseDesignItem('savedComment', newId, trimmedText, { questionLogic: questionID });
    setIsAddModalOpen(false);
    setNewCommentText('');
    handleCopyComment(trimmedText, newId);
  }, [canManageSavedComments, generateRandomId, handleCopyComment, newCommentText, questionID, savedComments, updateCourseDesignItem]);

  useEffect(() => {
    if (!isAddModalOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (addPopoverRef.current && !addPopoverRef.current.contains(event.target)) {
        saveNewComment();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModals();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        saveNewComment();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModals, isAddModalOpen, saveNewComment]);

  useEffect(() => {
    if (isAddModalOpen && addPopoverRef.current) {
      const textarea = addPopoverRef.current.querySelector('textarea');
      if (textarea) {
        adjustTextareaHeight(textarea);
        textarea.focus();
        textarea.select();
      }
    }
  }, [adjustTextareaHeight, isAddModalOpen]);

  useEffect(() => {
    if (isEditModalOpen && editPopoverRef.current) {
      const textarea = editPopoverRef.current.querySelector('textarea');
      if (textarea) {
        adjustTextareaHeight(textarea);
        textarea.focus();
        textarea.select();
      }
    }
  }, [adjustTextareaHeight, isEditModalOpen]);

  const handleUpdateSavedComment = useCallback(
    () => {
      if (!editCommentId) {
        return;
      }
      const draftText = editCommentText.trim();
      if (draftText.length === 0) {
        return;
      }

      updateCourseDesignItem('savedComment', editCommentId, draftText, { questionLogic: questionID });
      setIsEditModalOpen(false);
      setEditCommentId(null);
      setEditCommentText('');
    },
    [editCommentId, editCommentText, questionID, updateCourseDesignItem]
  );

  const handleDeleteSavedComment = useCallback(
    (commentId) => {
      if (!canManageSavedComments) {
        return;
      }

      const target = savedComments.find((comment) => comment.dataID === commentId);
      if (!target) {
        return;
      }
      removeFromDatabase('savedComment', commentId);
      if (commentId === editCommentId) {
        closeModals();
      }
    },
    [canManageSavedComments, closeModals, editCommentId, removeFromDatabase, savedComments]
  );

  const openAddModal = useCallback(() => {
    if (!canManageSavedComments) {
      return;
    }
    setIsEditModalOpen(false);
    setEditCommentId(null);
    setEditCommentText('');
    setNewCommentText('');
    setIsAddModalOpen(true);
  }, [canManageSavedComments]);

  const openEditModal = useCallback((comment) => {
    if (!canManageSavedComments || !comment) {
      return;
    }
    setIsAddModalOpen(false);
    setIsEditModalOpen(true);
    setEditCommentId(comment.dataID);
    setEditCommentText(comment.dataText || '');
  }, [canManageSavedComments]);

  const requestDeleteComment = useCallback((commentId) => {
    if (!canManageSavedComments) {
      return;
    }
    const target = savedComments.find((comment) => comment.dataID === commentId);
    if (!target) {
      return;
    }
    setPendingDeleteCommentId(commentId);
  }, [canManageSavedComments, savedComments]);

  const closeDeletePrompt = useCallback(() => {
    setPendingDeleteCommentId(null);
  }, []);

  const closeHistoryModal = useCallback(() => {
    setIsClosingHistory(true);
    setTimeout(() => {
      setShowHistoryModal(false);
      setIsClosingHistory(false);
    }, 220);
  }, []);


  if (!question) {
    return <div>Question not found</div>;
  }

  const questionText = question.dataText;

  return (
    <div className='questionContainer'>
      <p className='questionText longAnswerQuestionText'>
  {questionText.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ))}
</p>

      
      {answersReadOnly ? (
        <div>
          <div className='longAnswerTextForPDF'>
            {(() => {
              const existingAnswer = userAnswers.find(answer => answer.questionID === questionID);
              const { content } = separateContentAndReferences(existingAnswer?.questionEssayAnswer || '');
              return content;
            })()}
          </div>
          {hasReferences && (() => {
            const existingAnswer = userAnswers.find(answer => answer.questionID === questionID);
            const { references } = separateContentAndReferences(existingAnswer?.questionEssayAnswer || '');
            if (references && references.trim() !== '') {
              return (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '10px', 
                  backgroundColor: 'var(--color2)', 
                  borderRadius: '4px'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    whiteSpace: 'pre-wrap', 
                    color: 'white', 
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere'
                  }}>
                    {references}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      ) : (
        <>
          <AnswerTextArea 
            columnName={questionID}
            initialAnswer={localAnswer}
            onChange={handleEssayChange}
          />
          {showWordLimitWarning && hasWordLimits && wordLimit && (
            <div style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
              Essay word limit ({wordLimit} words) has been reached.
            </div>
          )}
          
          {(hasReferences || (showHistoryButton && !isGradingMode)) && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ flex: 1 }} />
              {hasReferences && (
                <button
                  onClick={() => setShowReferencesModal(true)}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: (() => {
                      const existingAnswer = userAnswers.find(answer => answer.questionID === questionID);
                      const { references } = separateContentAndReferences(existingAnswer?.questionEssayAnswer || '');
                      return references && references.trim() !== '' ? 'var(--color1)' : 'var(--color2)';
                    })(),
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-primary)'
                  }}
                >
                  References
                </button>
              )}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {showHistoryButton && !isGradingMode && (
                  <button
                    type="button"
                    className="questionHistoryButton"
                    onClick={() => setShowHistoryModal(true)}
                    aria-label="View your answer history"
                    title="View your answer history"
                    style={{ height: 'auto', padding: '6px 9px', fontSize: '12px' }}
                  >
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </button>
                )}
              </div>
            </div>
          )}

          <ReferencesModal
            isOpen={showReferencesModal}
            onClose={() => setShowReferencesModal(false)}
            onSave={handleSaveReferences}
            initialReferences={(() => {
              const existingAnswer = userAnswers.find(answer => answer.questionID === questionID);
              const { references } = separateContentAndReferences(existingAnswer?.questionEssayAnswer || '');
              return references;
            })()}
          />
        </>
      )}
      {gradeEdit && <LongAnswerGradeBox questionID={questionID} evaluationID={evaluationID} username={username} />}
      {gradeDisplay && <LongAnswerGradeDisplay questionID={questionID} evaluationID={evaluationID} username={username} />}

      {isGradingMode && (
        <div className="questionActionRow">
          {isGradingMode && (
            <div className="questionSavedComments">
              <button
                type="button"
                className="questionActionButton"
                onClick={openAddModal}
                aria-label="Add a saved comment"
                title="Add a saved comment"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
              {savedComments.map((comment) => (
                <button
                  key={comment.dataID}
                  type="button"
                  className={`questionActionButton${copiedCommentId === comment.dataID ? ' copied' : ''}`}
                  onClick={(event) => {
                    if (event.detail > 1) {
                      return;
                    }
                    handleCopyComment(comment.dataText, comment.dataID);
                  }}
                  onDoubleClick={() => openEditModal(comment)}
                  aria-label={`Copy saved comment: ${comment.dataText}`}
                  data-tooltip={comment.dataText}
                >
                  <FontAwesomeIcon icon={faCommentDots} />
                </button>
              ))}
            </div>
          )}
          {showHistoryButton && typeof onOpenHistory === 'function' && (
            <div className="questionHistoryButtonWrapper">
              <button
                type="button"
                className="questionHistoryButton"
                onClick={() => onOpenHistory(questionID)}
                aria-label={`View answer history (${historyCount} revision${historyCount !== 1 ? 's' : ''})`}
                title={`View answer history (${historyCount} revision${historyCount !== 1 ? 's' : ''})`}
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
                {historyCount}
              </button>
            </div>
          )}
        </div>
      )}

      {copyError && (
        <div className="savedCommentsMessage error" role="status">
          {copyError}
        </div>
      )}

      {isGradingMode && isAddModalOpen && (
        <div className="savedCommentOverlay">
          <div className="savedCommentPopover savedCommentPopover--add" ref={addPopoverRef}>
              <textarea
                id={`new-comment-${questionID}`}
                value={newCommentText}
                onChange={(event) => {
                  setNewCommentText(event.target.value);
                  adjustTextareaHeight(event.target);
                }}
                placeholder="Add a saved comment"
              />
            <div className="savedCommentPopoverControls">
              <button
                type="button"
                className="savedCommentIconButton"
                onClick={(event) => {
                  event.preventDefault();
                  closeModals();
                }}
                aria-label="Cancel adding saved comment"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        </div>
      )}

      {isGradingMode && isEditModalOpen && (
        <div className="savedCommentOverlay">
          <div className="savedCommentPopover savedCommentPopover--edit" ref={editPopoverRef}>
            <textarea
              value={editCommentText}
              onChange={(event) => {
                setEditCommentText(event.target.value);
                adjustTextareaHeight(event.target);
              }}
              placeholder="Edit saved comment"
            />
            <div className="savedCommentPopoverControls">
            <button
              type="button"
              className="savedCommentIconButton"
              onClick={handleUpdateSavedComment}
              aria-label="Save comment changes"
            >
              <FontAwesomeIcon icon={faSave} />
            </button>
            <button
              type="button"
              className="savedCommentIconButton"
              onClick={() => requestDeleteComment(editCommentId)}
              aria-label="Delete saved comment"
              disabled={!editCommentId}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteCommentId && (
        <div className="savedCommentOverlay">
          <div className="savedCommentConfirm">
            <div className="savedCommentConfirmTitle">Delete this saved comment?</div>
            <div className="savedCommentConfirmActions">
              <button
                type="button"
                className="savedCommentIconButton"
                onClick={() => {
                  handleDeleteSavedComment(pendingDeleteCommentId);
                  closeDeletePrompt();
                }}
                aria-label="Confirm delete saved comment"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
              <button
                type="button"
                className="savedCommentIconButton"
                onClick={closeDeletePrompt}
                aria-label="Cancel delete saved comment"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div
          className="studentHistoryModalOverlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeHistoryModal(); }}
        >
          <div className={`studentHistoryModalCard${isClosingHistory ? ' closing' : ''}`}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px 0' }}>
              <button
                type="button"
                onClick={closeHistoryModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: 'var(--color2)',
                  lineHeight: 1,
                }}
                aria-label="Close history"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <IndividualQuestionStudentHistory
              username={username}
              questionID={questionID}
              showSnapshotPicker={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default LongAnswerQuestion;
